import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Box, TextField, Button, Typography, IconButton, Snackbar, Alert as MuiAlert, Stack, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { assignmentService } from '../../services/api';
import { useDropzone } from 'react-dropzone';
import { Upload as UploadIcon } from '@mui/icons-material';

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const AssignmentSubmitForm = () => {
  const { assignment_id, courseId, ccrId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!assignment_id;
  
  const [formData, setFormData] = useState({
    assignment_id: '',
    description: '',
    attachments: []
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (isEditMode) {
      fetchAssignmentSubmitForm();
    }
  }, [assignment_id, courseId, ccrId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles(prev => [...prev, ...acceptedFiles]);
    },
    accept: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt'
  });
  const [submittedDate, setSubmittedDate] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchAssignmentSubmitForm = async () => {
    setFetchLoading(true);
    try {
      const response = await assignmentService.submitAssignment(assignment_id, courseId, ccrId);
      if (response.data) {
        const assignment = response.data.data;
        setFormData({
          assignment_id: assignment.assignment_id,
          course_id: assignment.courseId,
          ccr_id: assignment.ccrId,
          description: assignment.description,
          attachments: Array.isArray(assignment.attachments) ? assignment.attachments : [],
        });
        console.log(assignment.submission.data);
        if(assignment.submission_status ==3){
        setIsSubmitted(true);
        }else{
          setIsSubmitted(false);
        }
        setSubmittedDate(assignment.submission_date);

      }
    } catch (error) {
      console.error('Failed to load assignment data');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({
      ...prev,
      description: content
    }));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.description || formData.description.length === 0) {
    newErrors.description = 'Assignment description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};


  const handleSubmit = async (isDraft = false) => {
    if (!validateForm()) {
      setSnackbar({
          open: true,
          message: 'Please fill all required fields',
          severity: 'error'
      });
      return;
    }
    setLoading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('description', formData.description || '');
      formPayload.append('attachments', formData.attachments || '');
      formPayload.append('save', isDraft ? 'draft' : 'publish');
      formPayload.append('assignment_id', assignment_id);
      formPayload.append('course_id', courseId);
      formPayload.append('ccr_id', ccrId);
  
      files.forEach(file => {
        formPayload.append('attachments', file); 
      });
  
      const response = await assignmentService.saveSubmission(assignment_id,formPayload); 
      // Mark content as completed if not a draft
      if (!isDraft) {
        try {
          // Check if we're in an iframe (ContentPlayer)
          const isInIframe = window.self !== window.top;
          
          if (isInIframe) {
            // Send a message to the parent window (ContentPlayer)
            window.parent.postMessage({
              type: 'ASSIGNMENT_COMPLETED',
              ccrId: ccrId
            }, '*');
          }
        } catch (error) {
          console.error('Error marking content as completed:', error);
        }
      }

      setSnackbar({
        open: true,
        message: `Assignment ${isDraft ? 'saved as draft' : 'submitted'} successfully!`,
        severity: 'success'
      });
  
      navigate(`/assignments/preview/${assignment_id}/${courseId}/${ccrId}`);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: 'Error saving assignment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
            Submission Form
      </Typography>
          <Button
           variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/assignments/preview/${assignment_id}`)}
          >
           Back to Assignments
          </Button>
      </Box>
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(false); }}>
      
      <input 
        type="hidden" 
        name="courseId" 
        value={courseId || ''} 
      />
      <input 
        type="hidden" 
        name="ccrId" 
        value={ccrId || ''} 
      />
      
      <ReactQuill
        theme="snow"
        value={formData.description}
        onChange={handleDescriptionChange}
        style={{ height: '200px', marginBottom: '50px' }}
        readOnly={isSubmitted}
      />
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Attachments
          </Typography>

          {/* Existing attachments */}
          {formData.attachments.map((attachment, index) => {
            const fileUrl = `/uploads/assignment_submission/${attachment}`;
            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#1976d2' }}
                >
                  <Typography variant="body2">{attachment.split('/').pop()}</Typography>
                </a>
                {!isSubmitted && (
                  <IconButton color="error" onClick={() => removeAttachment(index)} sx={{ ml: 2 }}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {/* New attachments */}
          {files.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                New Attachments
              </Typography>
              <Stack spacing={1}>
                {files.map((file, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{file.name}</Typography>
                    <IconButton color="error" onClick={() => removeFile(index)} sx={{ ml: 2 }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
         {!isSubmitted && (
          <DropzoneArea {...getRootProps()}>
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Supported formats: PDF, Word, PowerPoint, Excel, Images, Text
            </Typography>
          </DropzoneArea>
        )}
      
        </Box>
      
        {!isSubmitted && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button type="submit" data-id={isSubmitted} variant="contained" color="primary" disabled={loading}>
            {loading ? 'Saving...' : 'Submit Assignment'}
          </Button>
        </Box>
      )}
      </form>
    </Box>
  );
};

export default AssignmentSubmitForm;
